import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { requestWithFallback } from '../api/httpClient';

const DEFAULT_SUGGESTIONS = [
    {
        title: 'Visitas pendientes o sin técnico hoy',
        subtitle: 'Revisar visitas atrasadas o próximas a vencer',
        prompt: '¿Qué visitas tenemos pendientes o sin técnico hoy?',
        personalized: false,
    },
    {
        title: 'Cumplimiento de contratos activos',
        subtitle: 'Ver contratos con menor cumplimiento este mes',
        prompt: '¿Qué contratos tienen menor cumplimiento este mes?',
        personalized: false,
    },
    {
        title: 'Simular asignación de técnicos',
        subtitle: 'Evaluar cobertura disponible para visitas pendientes',
        prompt: 'Simular asignación de técnicos para visitas pendientes',
        personalized: false,
    },
    {
        title: 'Desvíos de tiempo de la semana',
        subtitle: 'Detectar diferencias entre tiempo estimado y real',
        prompt: '¿Qué desvíos de tiempo tuvimos esta semana?',
        personalized: false,
    },
];

export default function AsistenteOperativoSheet({ open, onClose }) {
    const { user, token } = useAuth();
    const location = useLocation();
    const [sizeMode, setSizeMode] = useState('normal'); // normal | wide | fullscreen
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [pendingProposal, setPendingProposal] = useState(null);
    const [feedbackRecorded, setFeedbackRecorded] = useState({});
    const [suggestedPrompts, setSuggestedPrompts] = useState(DEFAULT_SUGGESTIONS);
    const [behaviorSummary, setBehaviorSummary] = useState('');

    const empresaNombre = user?.empresa?.nombre || user?.empresa_nombre || 'tu empresa';
    const botName = user?.empresa?.nombre_asistente_ia || `Asistente de ${empresaNombre}`;
    const botAvatarUrl = user?.empresa?.logo_asistente_ia_url || '/images/default-ai-bot.png';
    const userFirstName = useMemo(() => {
        const raw = (user?.name || 'Equipo').trim();
        return raw.split(' ')[0] || 'Equipo';
    }, [user?.name]);

    const screenEntity = useMemo(() => {
        const path = location?.pathname || '';
        const matchVisita = path.match(/\/visitas\/(\d+)/i);
        if (matchVisita) {
            return { type: 'visita', id: Number(matchVisita[1]), label: path };
        }
        return { type: 'general', id: null, label: path };
    }, [location?.pathname]);

    useEffect(() => {
        if (!open || !token) {
            return;
        }
        let cancelled = false;
        requestWithFallback({
            moduleName: 'visitas',
            endpoint: '/react/ai-assistant/suggestions',
            method: 'get',
            token,
        })
            .then(({ data }) => {
                if (cancelled) return;
                if (Array.isArray(data?.suggestions) && data.suggestions.length > 0) {
                    setSuggestedPrompts(data.suggestions);
                }
                if (typeof data?.behavior_summary === 'string') {
                    setBehaviorSummary(data.behavior_summary);
                }
            })
            .catch(() => {
                // Mantener sugerencias por defecto ante falta de red
            });

        return () => {
            cancelled = true;
        };
    }, [open, token]);

    if (!open) {
        return null;
    }

    const sendQuestion = async (rawPrompt) => {
        const text = String(rawPrompt ?? question).trim();
        if (!text || loading || !token) {
            return;
        }

        const nowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setMessages((prev) => [...prev, { role: 'user', content: text, timestamp: nowTime }]);
        setQuestion('');
        setLoading(true);

        try {
            const { data } = await requestWithFallback({
                moduleName: 'visitas',
                endpoint: '/react/ai-assistant/ask',
                method: 'post',
                token,
                data: {
                    prompt: text,
                    entity_type: screenEntity.type,
                    entity_id: screenEntity.id,
                    route_label: screenEntity.label,
                },
                dedupeInFlight: false,
            });

            if (data?.action_proposal) {
                setPendingProposal(data.action_proposal);
            }

            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: data?.response_text || 'Sin respuesta disponible.',
                    links: Array.isArray(data?.links) ? data.links : [],
                    log_id: data?.log_id || null,
                    timestamp: data?.timestamp || nowTime,
                },
            ]);
        } catch {
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: 'No se pudo procesar la consulta en este momento. Verificá tu conexión e intentá nuevamente.',
                    links: [],
                    log_id: null,
                    timestamp: nowTime,
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmProposal = async () => {
        if (!pendingProposal || loading || !token) {
            return;
        }
        const proposalToRun = pendingProposal;
        setPendingProposal(null);
        setLoading(true);
        const nowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        try {
            const { data } = await requestWithFallback({
                moduleName: 'visitas',
                endpoint: '/react/ai-assistant/confirm-action',
                method: 'post',
                token,
                data: { proposal: proposalToRun },
                dedupeInFlight: false,
            });

            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: data?.response_text || 'La tarea fue ejecutada correctamente.',
                    links: Array.isArray(data?.links) ? data.links : [],
                    log_id: null,
                    timestamp: data?.timestamp || nowTime,
                },
            ]);
        } catch (err) {
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: err?.response?.data?.response_text || 'No se pudo ejecutar la tarea solicitada.',
                    links: [],
                    log_id: null,
                    timestamp: nowTime,
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelProposal = () => {
        setPendingProposal(null);
        const nowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setMessages((prev) => [
            ...prev,
            {
                role: 'assistant',
                content: 'Tarea cancelada. No se realizó ningún cambio en el sistema.',
                links: [],
                log_id: null,
                timestamp: nowTime,
            },
        ]);
    };

    const handleFeedback = async (logId, value) => {
        if (!logId || !token) {
            return;
        }
        setFeedbackRecorded((prev) => ({ ...prev, [logId]: value }));
        try {
            await requestWithFallback({
                moduleName: 'visitas',
                endpoint: '/react/ai-assistant/feedback',
                method: 'post',
                token,
                data: { log_id: logId, feedback: value },
                dedupeInFlight: false,
            });
        } catch {
            // No bloqueante
        }
    };

    const panelStyle = useMemo(() => {
        if (sizeMode === 'fullscreen') {
            return {
                position: 'fixed',
                inset: '10px',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                background: '#FFFFFF',
                borderRadius: '18px',
                boxShadow: '0 24px 60px rgba(15, 23, 42, 0.32)',
                overflow: 'hidden',
            };
        }
        const width = sizeMode === 'wide' ? 'min(820px, calc(100vw - 20px))' : 'min(540px, calc(100vw - 20px))';
        return {
            position: 'fixed',
            right: '10px',
            bottom: '10px',
            width,
            height: 'min(720px, calc(100dvh - 20px))',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            background: '#FFFFFF',
            borderRadius: '20px',
            boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
            border: '1px solid #E2E8F0',
            overflow: 'hidden',
        };
    }, [sizeMode]);

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9998,
                background: sizeMode === 'fullscreen' ? 'rgba(15, 23, 42, 0.45)' : 'rgba(15, 23, 42, 0.2)',
            }}
            onClick={onClose}
        >
            <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
                {/* Cabecera del compañero virtual */}
                <div
                    style={{
                        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                        color: '#FFFFFF',
                        padding: '10px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <img
                            src={botAvatarUrl}
                            alt={botName}
                            style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '10px',
                                objectFit: 'cover',
                                background: '#FFFFFF',
                                border: '1.5px solid rgba(56, 189, 248, 0.5)',
                            }}
                        />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {botName}
                            </div>
                            <div style={{ fontSize: '10px', color: '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                En línea · {empresaNombre}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button
                            type="button"
                            onClick={() => setSizeMode((m) => (m === 'wide' ? 'normal' : 'wide'))}
                            style={{
                                background: 'rgba(255,255,255,0.1)',
                                color: '#E2E8F0',
                                border: '1px solid rgba(255,255,255,0.16)',
                                borderRadius: '6px',
                                padding: '4px 7px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            {sizeMode === 'wide' ? 'Normal' : 'Ampliar'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setSizeMode((m) => (m === 'fullscreen' ? 'normal' : 'fullscreen'))}
                            style={{
                                background: 'rgba(255,255,255,0.1)',
                                color: '#E2E8F0',
                                border: '1px solid rgba(255,255,255,0.16)',
                                borderRadius: '6px',
                                padding: '4px 7px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            {sizeMode === 'fullscreen' ? 'Ventana' : 'Completa'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: 'rgba(255,255,255,0.12)',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>

                {/* Cuerpo de conversación */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '10px 12px',
                        background: '#F8FAFC',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                    }}
                >
                    {messages.length === 0 ? (
                        <div>
                            <div
                                style={{
                                    background: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '10px',
                                    padding: '9px 11px',
                                    fontSize: '12px',
                                    color: '#1E293B',
                                    lineHeight: 1.4,
                                    marginBottom: '8px',
                                }}
                            >
                                Hola <strong>{userFirstName}</strong>. Soy <strong>{botName}</strong>, tu compañero de coordinación en{' '}
                                <strong>{empresaNombre}</strong>. Podés consultarme estado de visitas, contratos o pedirme preparar tareas operativas con tu confirmación.
                                {behaviorSummary ? (
                                    <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #E2E8F0', fontSize: '11px', color: '#64748B' }}>
                                        {behaviorSummary}
                                    </div>
                                ) : null}
                            </div>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '6px' }}>
                                Sugerencias adaptadas a tu actividad
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                {suggestedPrompts.map((item) => (
                                    <button
                                        key={item.prompt || item.title}
                                        type="button"
                                        onClick={() => sendQuestion(item.prompt)}
                                        style={{
                                            textAlign: 'left',
                                            padding: '7px 10px',
                                            borderRadius: '8px',
                                            background: '#FFFFFF',
                                            border: '1px solid #E2E8F0',
                                            fontSize: '11.5px',
                                            fontWeight: 600,
                                            color: '#334155',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div>{item.title || item.prompt}</div>
                                        {item.subtitle ? (
                                            <div style={{ fontSize: '10.5px', fontWeight: 400, color: '#64748B', marginTop: '1px' }}>
                                                {item.subtitle}
                                            </div>
                                        ) : null}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {messages.map((msg, idx) => (
                        <div
                            key={idx}
                            style={{
                                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                maxWidth: '88%',
                                background: msg.role === 'user' ? '#2563EB' : '#FFFFFF',
                                color: msg.role === 'user' ? '#FFFFFF' : '#1E293B',
                                border: msg.role === 'user' ? 'none' : '1px solid #E2E8F0',
                                borderRadius: '14px',
                                padding: '10px 13px',
                                fontSize: '13px',
                                lineHeight: 1.5,
                                whiteSpace: 'pre-line',
                            }}
                        >
                            <div>{msg.content}</div>
                            {msg.role === 'assistant' && msg.log_id ? (
                                <div
                                    style={{
                                        marginTop: '8px',
                                        paddingTop: '6px',
                                        borderTop: '1px solid #F1F5F9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        fontSize: '11px',
                                        color: '#94A3B8',
                                    }}
                                >
                                    <span>{msg.timestamp}</span>
                                    {feedbackRecorded[msg.log_id] ? (
                                        <span style={{ color: '#16A34A', fontWeight: 600 }}>Gracias por tu valoración</span>
                                    ) : (
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                type="button"
                                                onClick={() => handleFeedback(msg.log_id, 'util')}
                                                style={{
                                                    fontSize: '11px',
                                                    padding: '2px 8px',
                                                    borderRadius: '999px',
                                                    border: '1px solid #E2E8F0',
                                                    background: '#F8FAFC',
                                                    color: '#475569',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Útil
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleFeedback(msg.log_id, 'corregir')}
                                                style={{
                                                    fontSize: '11px',
                                                    padding: '2px 8px',
                                                    borderRadius: '999px',
                                                    border: '1px solid #E2E8F0',
                                                    background: '#F8FAFC',
                                                    color: '#475569',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                A mejorar
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    ))}

                    {pendingProposal ? (
                        <div
                            style={{
                                padding: '12px 14px',
                                borderRadius: '14px',
                                border: '1px solid #93C5FD',
                                background: '#EFF6FF',
                            }}
                        >
                            <div style={{ fontSize: '11px', fontWeight: 800, color: '#1D4ED8', textTransform: 'uppercase', marginBottom: '4px' }}>
                                Confirmación requerida antes de guardar cambios
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', marginBottom: '8px' }}>
                                {pendingProposal.title || 'Tarea operativa propuesta'}
                            </div>
                            {pendingProposal.details ? (
                                <div
                                    style={{
                                        background: '#FFFFFF',
                                        borderRadius: '10px',
                                        padding: '8px 10px',
                                        marginBottom: '10px',
                                        fontSize: '12px',
                                        border: '1px solid #DBEAFE',
                                    }}
                                >
                                    {Object.entries(pendingProposal.details).map(([label, val]) => (
                                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                            <span style={{ color: '#64748B', fontWeight: 600 }}>{label}:</span>
                                            <span style={{ color: '#0F172A', fontWeight: 600 }}>{String(val)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={handleConfirmProposal}
                                    disabled={loading}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '10px',
                                        background: '#2563EB',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Confirmar y ejecutar tarea
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancelProposal}
                                    disabled={loading}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '10px',
                                        background: '#FFFFFF',
                                        color: '#475569',
                                        border: '1px solid #CBD5E1',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Cancelar sin aplicar cambios
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {loading ? (
                        <div style={{ fontSize: '12px', color: '#64748B', fontStyle: 'italic' }}>
                            Analizando datos operativos de {empresaNombre}...
                        </div>
                    ) : null}
                </div>

                {/* Pie de consulta */}
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        sendQuestion();
                    }}
                    style={{
                        padding: '12px 14px',
                        borderTop: '1px solid #E2E8F0',
                        background: '#FFFFFF',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                    }}
                >
                    <input
                        type="text"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="Escribí tu consulta o tarea..."
                        disabled={loading}
                        style={{
                            flex: 1,
                            padding: '10px 12px',
                            borderRadius: '12px',
                            border: '1px solid #CBD5E1',
                            fontSize: '13px',
                            outline: 'none',
                        }}
                    />
                    <button
                        type="submit"
                        disabled={loading || !question.trim()}
                        style={{
                            padding: '10px 16px',
                            borderRadius: '12px',
                            background: '#2563EB',
                            color: '#FFFFFF',
                            border: 'none',
                            fontSize: '13px',
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        Enviar
                    </button>
                </form>
            </div>
        </div>
    );
}
